/**
 * useChatMetadataListeners Hook
 * 
 * React hook for managing chat metadata listeners in AppLayout.
 * Handles standalone spark metadata events (pin, edit, delete).
 * Always-on listeners active from login to logout.
 */

import { useEffect } from 'react';
import listenerManager from './index';

/**
 * Hook to manage chat metadata listeners
 * 
 * Subscribes to standalone spark metadata events on mount.
 * No parameters needed - uses context from ListenerManager.
 * Lifecycle: Mount with AppLayout -> Unmount on logout (via manager cleanup)
 */
export const useChatMetadataListeners = () => {
  useEffect(() => {
    let cancelled = false;

    // Wait for ListenerManager to finish initializing before subscribing.
    // Uses the actual in-flight initialize() promise (via whenInitialized())
    // instead of a fixed-timeout poll — see whenInitialized()'s doc comment
    // in index.js for the race condition this used to have.
    listenerManager.whenInitialized().then(() => {
      if (cancelled) return;
      listenerManager.subscribeChatMetadata();
    });

    // Cleanup on unmount (rare - typically cleaned up via manager.cleanup() on logout)
    return () => {
      cancelled = true;
      listenerManager.unsubscribeChatMetadata();
    };
  }, []); // Empty deps - mount once with AppLayout

  return null;
};

export default useChatMetadataListeners;
